import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs from 'dayjs'
import { useMemo, useState } from 'react'
import { toast } from 'react-toastify'
import { CustomInput, CustomSelect, selectTypes } from 'src/consumer/components/helperComponents'
import { useDateRangeLogic } from '../hooks/useDateRangeLogic'
import {
  collectAllReportsData,
  downloadPdfReports,
  emailPdfReports,
  sendPdfReportsToCustomRecipient,
  sendPdfReportsToFinanceAdmins,
  sendPdfReportsToPractitioner,
  sendPdfReportsToSupportUser,
} from '../services/exportServices'
import { EXPORT_TYPES } from '../util'
import { generateExcelReport } from '../utils/excelGenerators'
import { validateEmail } from '../utils/validationHelpers'
import DateRangeSelector from './DateRangeSelector'

export default function ExportPayoutReports({
  startDate,
  endDate,
  dataFetchingApiUrl,
  extraParams = {},
  payoutReportDataArrayToExcelFormatArrayBuilder,
  fileNamePrefix = 'Payout Reports',
  closeDialog,
  entityId,
  entityType,
  useLocalDateFormatForApiCalls = false,
  eventType,
  allEventTypes,
  exportType = EXPORT_TYPES.excel.id,
  paymentConfig,
  periodOptions = [],
  practitionerContact,
  enablePayoutRecipientSelection = false,
}) {
  const {
    dateRangeMode,
    setDateRangeMode,
    dialogStartDate,
    setDialogStartDate,
    customDateRange,
    setCustomDateRange,
    selectedPeriodRange,
    setSelectedPeriodRange,
    getReportDateRange,
    isValidDateRange,
    generateFileName,
  } = useDateRangeLogic(startDate, endDate, { paymentConfig, periodOptions })

  const [isFetching, setIsFetching] = useState(false)
  const [loadingPercentage, setLoadingPercentage] = useState(0)
  const [exportEventType, setExportEventType] = useState(eventType)
  const [emailToSendTo, setEmailToSendTo] = useState('')
  const [sendRazorpayNotifications, setSendRazorpayNotifications] = useState(false)
  const [sendRazorpayReminderNotifications, setSendRazorpayReminderNotifications] = useState(false)
  const [addTdsRelatedInfo, setAddTdsRelatedInfo] = useState(false)
  const [includeAllSessionsInReport, setIncludeAllSessionsInReport] = useState(false)
  const [includeNonAumhumAffiliations, setIncludeNonAumhumAffiliations] = useState(false)
  const [billDate, setBillDate] = useState(dayjs())
  const [hideGeneratedOn, setHideGeneratedOn] = useState(false)
  const [sendByEmail, setSendByEmail] = useState(Boolean(practitionerContact?.email))
  const [sendByWhatsapp, setSendByWhatsapp] = useState(Boolean(practitionerContact?.mobile))
  const [payoutRecipientTarget, setPayoutRecipientTarget] = useState('practitioner')
  const [customRecipientName, setCustomRecipientName] = useState('')
  const [customRecipientEmail, setCustomRecipientEmail] = useState('')
  const [customRecipientWhatsapp, setCustomRecipientWhatsapp] = useState('')

  const exportEventTypeObject = EXPORT_TYPES?.[exportType] || {}
  const isPractitionerSend = exportType === EXPORT_TYPES.practitioner_pdf.id
  const isSupportUserSend = exportType === EXPORT_TYPES.support_pdf.id
  const isPractitionerRecipient =
    !enablePayoutRecipientSelection || payoutRecipientTarget === 'practitioner'
  const isFinanceAdminsRecipient = enablePayoutRecipientSelection && payoutRecipientTarget === 'finance-admins'
  const isCustomRecipient = enablePayoutRecipientSelection && payoutRecipientTarget === 'custom'
  const selectedPayoutDeliveryModes = useMemo(() => {
    const modes = []
    if (isPractitionerRecipient) {
      if (sendByEmail && practitionerContact?.email) modes.push('mail')
      if (sendByWhatsapp && practitionerContact?.mobile) modes.push('whatsapp')
      return modes
    }
    if (sendByEmail) modes.push('mail')
    if (sendByWhatsapp) modes.push('whatsapp')
    return modes
  }, [
    isPractitionerRecipient,
    practitionerContact?.email,
    practitionerContact?.mobile,
    sendByEmail,
    sendByWhatsapp,
  ])
  const isValidEmail = useMemo(
    () => validateEmail(emailToSendTo, exportType),
    [emailToSendTo, exportType],
  )
  const isCustomEmailValid = useMemo(
    () => !isCustomRecipient || !sendByEmail || validateEmail(customRecipientEmail, EXPORT_TYPES.email_pdf.id),
    [customRecipientEmail, isCustomRecipient, sendByEmail],
  )
  const isCustomWhatsappValid = useMemo(
    () => !isCustomRecipient || !sendByWhatsapp || customRecipientWhatsapp.trim().length > 0,
    [customRecipientWhatsapp, isCustomRecipient, sendByWhatsapp],
  )
  const canSendPayoutRecipient =
    !isPractitionerSend ||
    (selectedPayoutDeliveryModes.length > 0 && isCustomEmailValid && isCustomWhatsappValid)

  function handlePayoutRecipientTargetChange(event) {
    setPayoutRecipientTarget(event.target.value)
    if (!sendByEmail && !sendByWhatsapp) {
      setSendByEmail(true)
    }
  }

  async function downloadExcelReports({ reportDateMoment, reportEndDateMoment }) {
    const fetchParams = {
      reportDateMoment,
      reportEndDateMoment,
      dataFetchingApiUrl,
      extraParams,
      exportEventType,
      entityType,
      useLocalDateFormatForApiCalls,
      setLoadingPercentage,
    }

    const collectedData = await collectAllReportsData(fetchParams)
    if (!collectedData) return

    const transformedReportData = collectedData.map((data) =>
      payoutReportDataArrayToExcelFormatArrayBuilder(data),
    )

    const fileName = generateFileName(fileNamePrefix)
    generateExcelReport(transformedReportData, fileName)
  }
  async function handleActionButtonClick() {
    setIsFetching(true)
    setLoadingPercentage(0)

    const { start: reportDateMoment, end: reportEndDateMoment } = getReportDateRange

    try {
      switch (exportType) {
        case EXPORT_TYPES.excel.id:
          await downloadExcelReports({ reportDateMoment, reportEndDateMoment })
          break
        case EXPORT_TYPES.email_pdf.id: {
          const reportsEmailResponse = await emailPdfReports({
            reportDateMoment,
            reportEndDateMoment,
            entityId,
            entityType,
            exportEventType,
            emailToSendTo,
            sendRazorpayNotifications,
            sendRazorpayReminderNotifications,
            addTdsRelatedInfo,
            includeAllSessionsInReport,
            billDate,
            hideGeneratedOn,
            includeNonAumhumAffiliations,
          })
          if (!reportsEmailResponse) return
          break
        }
        case EXPORT_TYPES.pdf.id: {
          const reportsPdfResponse = await downloadPdfReports({
            reportDateMoment,
            reportEndDateMoment,
            entityId,
            entityType,
            exportEventType,
            sendRazorpayNotifications,
            sendRazorpayReminderNotifications,
            addTdsRelatedInfo,
            includeAllSessionsInReport,
            billDate,
            hideGeneratedOn,
            includeNonAumhumAffiliations,
          })
          if (!reportsPdfResponse) return
          break
        }
        case EXPORT_TYPES.practitioner_pdf.id: {
          let practitionerSendResponse
          if (isFinanceAdminsRecipient) {
            practitionerSendResponse = await sendPdfReportsToFinanceAdmins({
              reportDateMoment,
              reportEndDateMoment,
              entityId,
              entityType,
              exportEventType,
              deliveryModes: selectedPayoutDeliveryModes,
              sendRazorpayNotifications,
              sendRazorpayReminderNotifications,
              addTdsRelatedInfo,
              includeAllSessionsInReport,
              billDate,
              hideGeneratedOn,
              includeNonAumhumAffiliations,
            })
          } else if (isCustomRecipient) {
            practitionerSendResponse = await sendPdfReportsToCustomRecipient({
              reportDateMoment,
              reportEndDateMoment,
              entityId,
              entityType,
              exportEventType,
              deliveryModes: selectedPayoutDeliveryModes,
              customRecipient: {
                name: customRecipientName,
                email: customRecipientEmail,
                whatsapp: customRecipientWhatsapp,
              },
              sendRazorpayNotifications,
              sendRazorpayReminderNotifications,
              addTdsRelatedInfo,
              includeAllSessionsInReport,
              billDate,
              hideGeneratedOn,
              includeNonAumhumAffiliations,
            })
          } else {
            practitionerSendResponse = await sendPdfReportsToPractitioner({
              reportDateMoment,
              reportEndDateMoment,
              entityId,
              entityType,
              exportEventType,
              deliveryModes: selectedPayoutDeliveryModes,
              sendRazorpayNotifications,
              sendRazorpayReminderNotifications,
              addTdsRelatedInfo,
              includeAllSessionsInReport,
              billDate,
              hideGeneratedOn,
              includeNonAumhumAffiliations,
            })
          }
          if (!practitionerSendResponse) return
          toast.success(
            isFinanceAdminsRecipient
              ? 'Report queued for finance admins.'
              : isCustomRecipient
                ? 'Report queued for custom recipient.'
                : 'Report queued for practitioner.',
          )
          closeDialog?.()
          break
        }
        case EXPORT_TYPES.support_pdf.id: {
          const supportSendResponse = await sendPdfReportsToSupportUser({
            reportDateMoment,
            reportEndDateMoment,
            entityId,
            entityType,
            exportEventType,
            deliveryModes: ['mail', 'whatsapp'],
            sendRazorpayNotifications,
            sendRazorpayReminderNotifications,
            addTdsRelatedInfo,
            includeAllSessionsInReport,
            billDate,
            hideGeneratedOn,
            includeNonAumhumAffiliations,
          })
          if (!supportSendResponse) return
          toast.success('Report queued for you.')
          closeDialog?.()
          break
        }
        default:
          return
      }
    } catch (error) {
      toast.error('Something went wrong while preparing the reports.')
      console.error(error)
    } finally {
      setIsFetching(false)
    }
  }

  return (
    <>
      <DialogTitle>
        {enablePayoutRecipientSelection && isPractitionerSend
          ? 'Send Payout Report'
          : `Export Reports (${exportEventTypeObject?.label})`}
      </DialogTitle>
      <DialogContent sx={{ flex: 1, overflow: 'auto' }}>
        <div className="d-flex flex-column gap-3">
          <DateRangeSelector
            dateRangeMode={dateRangeMode}
            setDateRangeMode={setDateRangeMode}
            dialogStartDate={dialogStartDate}
            setDialogStartDate={setDialogStartDate}
            customDateRange={customDateRange}
            setCustomDateRange={setCustomDateRange}
            selectedPeriodRange={selectedPeriodRange}
            setSelectedPeriodRange={setSelectedPeriodRange}
            paymentConfig={paymentConfig}
            periodOptions={periodOptions}
            isFetching={isFetching}
            isValidDateRange={isValidDateRange}
          />
          {allEventTypes && allEventTypes.length > 0 && (
            <CustomSelect
              id="event-type"
              options={allEventTypes}
              value={exportEventType}
              getOptionLabel={(option) => option.title}
              getOptionValue={(option) => option.id}
              menuPortalTarget={document.body}
              styles={{ menuPortal: (base) => ({ ...base, zIndex: 99999 }) }}
              onChangeEvent={(e) => setExportEventType(e.target.value)}
              label={'Event Type'}
              selectType={selectTypes.string}
            />
          )}
          
          <DatePicker
            label="Bill Date"
            value={billDate}
            onChange={(newValue) => setBillDate(newValue)}
            slotProps={{
              textField: {
                fullWidth: true,
                variant: "outlined",
                size: "medium"
              }
            }}
          />
          
          <FormControlLabel
            control={
              <Checkbox
                checked={hideGeneratedOn}
                onChange={(e) => setHideGeneratedOn(e.target.checked)}
              />
            }
            label="Hide Generated On"
          />
          {exportType === EXPORT_TYPES.email_pdf.id && (
            <CustomInput
              label="Email to send to"
              type="email"
              value={emailToSendTo}
              onChange={(e) => setEmailToSendTo(e.target.value)}
            />
          )}
          {isPractitionerSend && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {enablePayoutRecipientSelection && (
                <>
                  <Typography variant="subtitle2">Recipient</Typography>
                  <RadioGroup
                    value={payoutRecipientTarget}
                    onChange={handlePayoutRecipientTargetChange}
                  >
                    <FormControlLabel
                      value="practitioner"
                      control={<Radio />}
                      label="Practitioner"
                    />
                    <FormControlLabel
                      value="finance-admins"
                      control={<Radio />}
                      label="Finance admins"
                    />
                    <FormControlLabel
                      value="custom"
                      control={<Radio />}
                      label="Custom email / WhatsApp"
                    />
                  </RadioGroup>
                </>
              )}
              {isPractitionerRecipient && (
                <>
                  <Typography variant="subtitle2">Send to practitioner</Typography>
                  {practitionerContact?.email && (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={sendByEmail}
                          onChange={(e) => setSendByEmail(e.target.checked)}
                        />
                      }
                      label={`Email: ${practitionerContact.email}`}
                    />
                  )}
                  {practitionerContact?.mobile && (
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={sendByWhatsapp}
                          onChange={(e) => setSendByWhatsapp(e.target.checked)}
                        />
                      }
                      label={`WhatsApp: ${practitionerContact.mobile}`}
                    />
                  )}
                  {!practitionerContact?.email && !practitionerContact?.mobile && (
                    <Typography variant="body2" color="text.secondary">
                      No practitioner email or WhatsApp number is available.
                    </Typography>
                  )}
                </>
              )}
              {isFinanceAdminsRecipient && (
                <>
                  <Typography variant="subtitle2">Send to finance admins</Typography>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={sendByEmail}
                        onChange={(e) => setSendByEmail(e.target.checked)}
                      />
                    }
                    label="Email finance admins"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={sendByWhatsapp}
                        onChange={(e) => setSendByWhatsapp(e.target.checked)}
                      />
                    }
                    label="WhatsApp finance admins"
                  />
                  <Typography variant="caption" color="text.secondary">
                    Recipients are enabled users with ROLE_FINANCE_ADMIN or ROLE_FINANCE.
                  </Typography>
                </>
              )}
              {isCustomRecipient && (
                <>
                  <Typography variant="subtitle2">Custom recipient</Typography>
                  <CustomInput
                    label="Recipient name"
                    value={customRecipientName}
                    onChange={(e) => setCustomRecipientName(e.target.value)}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={sendByEmail}
                        onChange={(e) => setSendByEmail(e.target.checked)}
                      />
                    }
                    label="Send email"
                  />
                  {sendByEmail && (
                    <CustomInput
                      label="Custom email"
                      type="email"
                      value={customRecipientEmail}
                      onChange={(e) => setCustomRecipientEmail(e.target.value)}
                    />
                  )}
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={sendByWhatsapp}
                        onChange={(e) => setSendByWhatsapp(e.target.checked)}
                      />
                    }
                    label="Send WhatsApp"
                  />
                  {sendByWhatsapp && (
                    <CustomInput
                      label="Custom WhatsApp number"
                      value={customRecipientWhatsapp}
                      onChange={(e) => setCustomRecipientWhatsapp(e.target.value)}
                    />
                  )}
                  {(!isCustomEmailValid || !isCustomWhatsappValid) && (
                    <Typography variant="caption" color="error">
                      Enter a valid email and/or WhatsApp number for the selected channel.
                    </Typography>
                  )}
                </>
              )}
            </Box>
          )}
          {isSupportUserSend && (
            <Typography variant="body2" color="text.secondary">
              The generated report will be sent to your configured support email and WhatsApp when available.
            </Typography>
          )}
          {(exportType === EXPORT_TYPES.email_pdf.id ||
            exportType === EXPORT_TYPES.pdf.id ||
            isPractitionerSend ||
            isSupportUserSend) && (
            <div className="d-flex flex-column gap-1 mt-2">
              {entityType === 'mentor' && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={addTdsRelatedInfo}
                      onChange={(e) => setAddTdsRelatedInfo(e.target.checked)}
                    />
                  }
                  label="Add TDS related info in the report"
                />
              )}
              {entityType === 'mentor' && (
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={includeAllSessionsInReport}
                      onChange={(e) => setIncludeAllSessionsInReport(e.target.checked)}
                    />
                  }
                  label="Include all sessions in the report"
                />
              )}
              {entityType === 'clinic-brand' && (
                <>
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={includeNonAumhumAffiliations}
                        onChange={(e) => setIncludeNonAumhumAffiliations(e.target.checked)}
                      />
                    }
                    label="Include clinic-associated practitioner sessions in PDF"
                  />
                  <Typography variant="caption" color="text.secondary">
                    These rows stay excluded from payout calculations and are added only as highlighted reference rows.
                  </Typography>
                </>
              )}
              <strong>Razorpay Notifications</strong>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={sendRazorpayNotifications}
                    onChange={(e) => setSendRazorpayNotifications(e.target.checked)}
                  />
                }
                label="Send Razorpay notifications"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={sendRazorpayReminderNotifications}
                    onChange={(e) => setSendRazorpayReminderNotifications(e.target.checked)}
                  />
                }
                label="Send Razorpay reminder notifications"
              />
            </div>
          )}
        </div>
      </DialogContent>
      <DialogActions>
        <Button color="unFocused" disabled={isFetching} onClick={closeDialog}>
          Cancel
        </Button>
        <div className="d-flex align-items-center gap-1">
          <Button
            variant="contained"
            disabled={isFetching || !isValidEmail || !isValidDateRange || !canSendPayoutRecipient}
            onClick={handleActionButtonClick}
          >
            {isFetching ? 'Preparing Reports...' : exportEventTypeObject?.actionButtonText}
          </Button>
          {isFetching && (
            <CircularProgressWithLabel
              size="small"
              variant="determinate"
              value={loadingPercentage * 100}
            />
          )}
        </div>
      </DialogActions>
    </>
  )
}

function CircularProgressWithLabel(props) {
  return (
    <div style={{ position: 'relative', display: 'inline-flex', width: '30px' }}>
      <CircularProgress variant="determinate" {...props} sx={{ width: '100%' }} />
      <div
        style={{
          top: 0,
          left: 0,
          bottom: 0,
          right: 0,
          position: 'absolute',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Typography variant="caption" component="div" sx={{ color: 'text.secondary' }}>
          {`${Math.round(props.value)}%`}
        </Typography>
      </div>
    </div>
  )
}
